import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import useAxios from "axios-hooks";
import * as yup from "yup";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import { toastState } from "atoms/toastState";
import Card from "components/card/Card";
import { createConnectorDrawerState } from "atoms/createConnectorDrawerState";
import logger from "infra/logger";
import {
  createChargePointConnectorSelectOptions,
  formatTypeMapping,
} from "../variables/chargePointConnectorSelectOptions";

const CreateConnectorDrawer = (props) => {
  const setToast = useSetRecoilState(toastState);
  const [drawerState, setDrawerState] = useRecoilState(
    createConnectorDrawerState,
  );
  const [{ data, loading: createLoading, error }, CreateConnector] = useAxios(
    { method: "POST", url: `/api/evses/create` },
    { manual: true },
  );

  const validationSchema = yup.object().shape({
    EvseSerialId: yup.string().min(1).required("Code is required"),
    Standard: yup
      .string()
      .oneOf(
        ["AC Type 1", "AC Type 2", "CCS Type 2", "CHAdeMO", "DOMESTIC_D"],
        "Select a valid option",
      )
      .required("Type of Evse is required"),
    Format: yup
      .string()
      .oneOf(["SOCKET", "CABLE"], "Select a valid option")
      .required("Format Code is required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
    watch,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  // Watch format field changes
  const formatValue = watch("Format");

  const closeHandler = () => {
    setDrawerState(null);
  };

  const createHandler = async () => {
    const formValues = getValues();
    let data = {};

    if (formValues.EvseSerialId.trim() !== "") {
      data["EvseSerialId"] = parseInt(formValues.EvseSerialId.trim());
    }

    if (formValues.Standard.trim() !== "") {
      data["Standard"] = formValues.Standard.trim();
    }
    if (formValues.Format.trim() !== "") {
      data["Format"] = formValues.Format.trim();
    }

    if (Object.keys(data).length === 3) {
      data["StationId"] = parseInt(props?.chargePointId);
      try {
        const response = await CreateConnector({ data: data });
        setToast({
          isOpen: true,
          title: "Connector Created Sucessfully",
          status: "success",
        });
        props.update();
      } catch (error) {
        const err = error?.response?.data?.error;
        logger.log(error);
        setToast({
          isOpen: true,
          title: "Error occured when creating Connector",
          description: err ? err : null,
          status: "error",
        });
      }
    } else {
      setToast({
        isOpen: true,
        title: "Enter all the fields to create Connector",
        status: "warning",
        position: "top",
      });
    }
  };

  return (
    <Drawer
      isOpen={drawerState?.isOpen}
      placement="right"
      onClose={closeHandler}
      size="md"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Add New Connector</DrawerHeader>
        <DrawerBody>
          <Card>
            <form>
              {createChargePointConnectorSelectOptions.map((item, index) => {
                // Get filtered options for Type of Evse based on selected format
                let optionsToShow = item.options;
                if (item.name === "Standard" && formatValue) {
                  optionsToShow = formatTypeMapping[formatValue] || [];
                }

                return (
                  <Flex key={index} mb="20px" alignItems="center">
                    <Controller
                      name={item.name}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <FormControl
                          isRequired
                          flexDirection="row"
                          isInvalid={errors[item.name]}
                        >
                          <FormLabel fontSize={16}>{item.label}</FormLabel>
                          {item.type === "select" ? (
                            <Select
                              {...field}
                              focusBorderColor="#f57d36"
                              fontSize="sm"
                              width={{ base: "100%", md: "400px" }}
                              height="50px"
                              onChange={(e) => {
                                field.onChange(e);
                                // Reset Standard field when Format changes
                                if (item.name === "Format") {
                                  setValue("Standard", "");
                                }
                              }}
                              isDisabled={
                                item.name === "Standard" && !formatValue
                              }
                            >
                              <option value="">Select</option>
                              {optionsToShow.map((option, ind) => {
                                return (
                                  <option key={ind} value={option.value}>
                                    {option.name}
                                  </option>
                                );
                              })}
                            </Select>
                          ) : (
                            <Input
                              {...field}
                              focusBorderColor="#f57d36"
                              fontSize="sm"
                              placeholder={item.placeholder}
                              width={{ base: "100%", md: "400px" }}
                              height="50px"
                              type={item.type}
                              isRequired
                            />
                          )}
                          <FormHelperText color={"#f57d36"}>
                            {item.helper}
                          </FormHelperText>
                          <FormErrorMessage mb={"20px"}>
                            {errors[item.name] && errors[item.name].message}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  </Flex>
                );
              })}
            </form>
          </Card>
        </DrawerBody>
        <DrawerFooter flexWrap="wrap" gap={2} justifyContent="flex-end">
          <Button
            variant="outline"
            onClick={closeHandler}
            flex={{ base: "1", sm: "0 1 auto" }}
          >
            Cancel
          </Button>
          <Button
            bg="#f57d36"
            color="white"
            colorScheme="orange"
            onClick={handleSubmit(createHandler)}
            isLoading={createLoading}
            flex={{ base: "1", sm: "0 1 auto" }}
          >
            Create
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default CreateConnectorDrawer;
